"use client";

import { useLocale, useTranslations } from "next-intl";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import type { ProductCardData } from "./product-card";

export type ProductCardPriceData = Pick<
	ProductCardData,
	| "price"
	| "currency"
	| "locale"
	| "channel"
	| "pricePresentation"
	| "compareAtPrice"
	| "compareAtPresentation"
	| "vialsPerSaleUnit"
	| "perVialPresentation"
	| "isStartingPrice"
>;

function fallback(amount: number, currency: string, locale?: string) {
	return new Intl.NumberFormat(locale ?? "en", { style: "currency", currency })
		.format(amount)
		.replace(/\u202F/g, "\u00A0");
}

function Money({
	presentation,
	amount,
	currency,
	locale,
	channel,
	className,
}: {
	presentation?: ProductCardData["pricePresentation"] | null;
	amount: number;
	currency: string;
	locale?: string;
	channel?: string;
	className?: string;
}) {
	return presentation && locale && channel ? (
		<ReferencePrimaryPrice
			presentation={presentation}
			locale={locale}
			channel={channel}
			className={className}
			showReferenceTooltip={false}
		/>
	) : (
		<span className={className}>{fallback(amount, currency, locale)}</span>
	);
}

export function ProductCardPrice({ product }: { product: ProductCardPriceData }) {
	const t = useTranslations("kitWebsite.unit");
	const providerLocale = useLocale();
	const activeLocale = product.locale ?? providerLocale;
	const isBox = Number.isInteger(product.vialsPerSaleUnit) && product.vialsPerSaleUnit! > 1;
	const kitLabel = t("price", { price: fallback(product.price, product.currency, activeLocale) });

	if (isBox) {
		const count =
			product.vialsPerSaleUnit === 10
				? t("short")
				: t("summary", { count: 1, vials: product.vialsPerSaleUnit! });
		const perVialAmount =
			product.perVialPresentation?.primaryLabel ??
			fallback(product.price / product.vialsPerSaleUnit!, product.currency, activeLocale);
		return (
			<div className="mt-auto min-w-0 pt-2">
				<div className="bg-primary/5 rounded-md px-2.5 py-2">
					<div className="min-w-0 text-primary">
						<span className="text-xl font-bold tabular-nums tracking-tight">
							{t("perVial", { price: perVialAmount })}
						</span>
					</div>
					<div className="mt-0.5 flex flex-wrap items-baseline gap-x-1.5 text-[11px] text-muted-foreground">
						<span>{kitLabel}</span>
						{product.compareAtPrice ? (
							<Money
								presentation={product.compareAtPresentation}
								amount={product.compareAtPrice}
								currency={product.currency}
								locale={product.locale}
								channel={product.channel}
								className="tabular-nums line-through"
							/>
						) : null}
						<span aria-hidden>·</span>
						<span>{count}</span>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="mt-auto flex min-w-0 items-baseline gap-2 pt-2">
			<Money
				presentation={product.pricePresentation}
				amount={product.price}
				currency={product.currency}
				locale={product.locale}
				channel={product.channel}
				className="text-base font-bold tabular-nums tracking-tight"
			/>
			{product.compareAtPrice ? (
				<Money
					presentation={product.compareAtPresentation}
					amount={product.compareAtPrice}
					currency={product.currency}
					locale={product.locale}
					channel={product.channel}
					className="text-[13px] tabular-nums text-muted-foreground line-through"
				/>
			) : null}
		</div>
	);
}
